import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebaseConfig';
import { ThreadMessage } from '../types';

interface ParticipantThreads {
  /** All messages grouped by submissionId, each array sorted oldest-first */
  bySubmission: Map<string, ThreadMessage[]>;
  /** Unread researcher messages per submissionId */
  unreadCounts: Map<string, number>;
  /** Total unread across all submissions */
  totalUnread: number;
  loading: boolean;
}

/**
 * Single real-time listener for ALL thread messages on a participant's submissions.
 * Groups by submissionId client-side. Replaces per-card listeners.
 */
export function useParticipantThreads(
  participantId: string | null,
  projectId: string | null,
  organizationId?: string | null,
): ParticipantThreads {
  const [messages, setMessages] = useState<ThreadMessage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!participantId || !projectId || !organizationId) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const q = query(
      collection(db, 'threadMessages'),
      where('participantId', '==', participantId),
      where('projectId', '==', projectId),
      where('organizationId', '==', organizationId),
      orderBy('createdAt', 'asc'),
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const msgs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as ThreadMessage));
        setMessages(msgs);
        setLoading(false);
      },
      (err) => console.error('[FS_DENY:useParticipantThreads]', { participantId, projectId, organizationId, err }),
    );

    return () => unsubscribe();
  }, [participantId, projectId, organizationId]);

  const bySubmission = useMemo(() => {
    const map = new Map<string, ThreadMessage[]>();
    for (const msg of messages) {
      const arr = map.get(msg.submissionId);
      if (arr) arr.push(msg);
      else map.set(msg.submissionId, [msg]);
    }
    return map;
  }, [messages]);

  const unreadCounts = useMemo(() => {
    const map = new Map<string, number>();
    for (const msg of messages) {
      if (msg.senderRole === 'researcher' && !msg.readByRecipient) {
        map.set(msg.submissionId, (map.get(msg.submissionId) || 0) + 1);
      }
    }
    return map;
  }, [messages]);

  const totalUnread = useMemo(() => {
    let total = 0;
    unreadCounts.forEach(c => { total += c; });
    return total;
  }, [unreadCounts]);

  return { bySubmission, unreadCounts, totalUnread, loading };
}
